import clsx from "clsx";
import { usePathname } from "next/navigation";
import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useState,
  useEffect,
} from "react";

import HamburgerIcon from "@ui/components/icon/HamburgerIcon";
import CommentCount, {
  Props as CommentCountProps,
} from "@ui/components/meta/CommentCount";
import StickyNavAltTitleContent from "@ui/components/navigation/Navigation/components/StickyNav/StickyNavAltTitleContent";
import StickyNavLoopNavContent from "@ui/components/navigation/Navigation/components/StickyNav/StickyNavLoopNavContent";
import StickyNavStandardContent from "@ui/components/navigation/Navigation/components/StickyNav/StickyNavStandardContent";
import NotificationsButton from "@ui/components/navigation/Notifications/NotificationsButton";
import SELECTORS from "@ui/config/class-selectors.config";
import { useNavigationContext } from "@ui/context/navigation-context";
import { sharedStyles } from "@ui/shared-styles";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import {
  Campaign,
  useCampaignContext,
} from "@voxmedia/duet-context/context/campaign-context";
import { useLoopNavContext } from "@voxmedia/duet-context/context/loop-nav-context";
import { useOptionalNotificationsDrawerContext } from "@voxmedia/duet-context/context/notifications-context";

import * as styles from "./StickyNav.css";

interface Props {
  Logo: FunctionComponent<{ className?: string }>;
  MinimalLogo?: FunctionComponent<{ className?: string }>;
  NotificationsLogo?: FunctionComponent<{ className?: string }>;
  headline?: string;
  isBranded?: boolean;
  overrideStickyVisible?: boolean;
  onSetOverrideStickyVisible?: (visible: boolean) => void;
  forceOpenLoopNav?: boolean;
  commentCountProps?: CommentCountProps;
  renderCommentCount?: (className: string) => ReactNode;
  className?: string;
  showNotificationsButton?: boolean;
  stickyVisibleThroughBottom?: boolean;
  showStickyOnCommentCountClick?: boolean;
  streamHeadline?: string;
  streamUrl?: string;
  packageHeadline?: string;
  packageUrl?: string;
  isLive?: boolean;
  enableAltTitleInNav?: boolean;
  notificationsClass?: string;
}

const StickyNav: FunctionComponent<Props> = ({
  Logo,
  MinimalLogo,
  NotificationsLogo,
  headline,
  isBranded = false,
  overrideStickyVisible,
  onSetOverrideStickyVisible,
  forceOpenLoopNav,
  commentCountProps,
  renderCommentCount,
  className,
  showNotificationsButton = false,
  stickyVisibleThroughBottom = false,
  showStickyOnCommentCountClick = false,
  streamHeadline,
  streamUrl,
  packageHeadline,
  packageUrl,
  isLive,
  enableAltTitleInNav,
  notificationsClass,
}) => {
  const pathname = usePathname();
  const [sticky, setSticky] = useState(false);
  const [currentHash, setCurrentHash] = useState("");
  const {
    openDrawer,
    drawerId,
    addArticleRefSubscriber,
    stickyNavTriggerClassName,
    navigationOverrides,
  } = useNavigationContext();
  const notificationsDrawer = useOptionalNotificationsDrawerContext();

  const { campaign } = useCampaignContext();
  const { loopNavData } = useLoopNavContext();
  const analytics = useAnalytics();

  const [activeCampaign, setActiveCampaign] = useState<Campaign | undefined>(
    campaign,
  );

  const handleOverrideSticky = useCallback(() => {
    setSticky(true);
  }, []);

  // This makes sure that when the override sticky visible is true,
  // the sticky nav is shown
  useEffect(() => {
    if (!overrideStickyVisible) {
      return;
    }
    handleOverrideSticky();
  }, [overrideStickyVisible, handleOverrideSticky]);

  // This makes sure that when sticky is set to false on scroll,
  // the override is reset to false so we don't show a double nav on
  // scroll back up after being in the comments when showStickyOnCommentCountClick is true
  useEffect(() => {
    if (sticky) {
      return;
    }
    if (onSetOverrideStickyVisible) {
      onSetOverrideStickyVisible(false);
    }
  }, [sticky, onSetOverrideStickyVisible]);

  useEffect(() => {
    // This effect runs on initial load and when the pathname changes
    setCurrentHash(window.location.hash);

    const handleHashChange = () => {
      setCurrentHash(window.location.hash);
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, [pathname]);

  // This effect makes sure that sticky nav is shown when comments is clicked
  // from another page (if it should be shown)
  useEffect(() => {
    if (currentHash === "#comments") {
      setTimeout(() => {
        if (showStickyOnCommentCountClick) {
          setSticky(true);
        }
      }, 100);
    }
  }, [currentHash, showStickyOnCommentCountClick]);

  useEffect(() => {
    const overrideCampaign = navigationOverrides?.campaign;
    if (overrideCampaign === "none") {
      setActiveCampaign(undefined);
      return;
    }
    if (overrideCampaign) {
      setActiveCampaign(overrideCampaign);
    }
  }, [navigationOverrides?.campaign]);

  const showLoopNav = loopNavData && !isBranded && !activeCampaign;

  useEffect(() => {
    const startObserver = new IntersectionObserver(([entry]) => {
      if (entry) {
        const inView = entry.boundingClientRect.bottom < 0;
        setSticky(inView);
        if (showLoopNav && inView) {
          analytics?.emit({
            event: "loopnav",
            loopnav: {
              action: "view",
              loopnav_name: loopNavData.title,
            },
          });
        }
      }
    });

    const exitObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          if (stickyVisibleThroughBottom) {
            return;
          }
          setSticky(
            entry.boundingClientRect.top < 0 && entry.intersectionRect.top > 0,
          );
        }
      },
      // Hide StickyNav when the bottom of the article intersects with the bottom of the viewport.
      {
        rootMargin: stickyVisibleThroughBottom
          ? "0px 0px 0px 0px"
          : "-100% 0px 0px 0px",
      },
    );

    const unsubscribe = addArticleRefSubscriber((articleElement) => {
      if (!articleElement || typeof IntersectionObserver === "undefined") {
        return;
      }

      const startElement = articleElement.querySelector(
        `.${stickyNavTriggerClassName}`,
      );

      if (!startElement) {
        return;
      }

      startObserver.observe(startElement);
      exitObserver.observe(articleElement);
    });

    return () => {
      unsubscribe();
      startObserver.disconnect();
      exitObserver.disconnect();
    };
  }, [
    addArticleRefSubscriber,
    stickyNavTriggerClassName,
    analytics,
    loopNavData?.title,
    showLoopNav,
    stickyVisibleThroughBottom,
  ]);

  const handleMoreBtnClick = () => {
    notificationsDrawer?.closeDrawer();
    openDrawer();
    analytics?.emit({
      event: "navigation_click",
      navigation_click: { name: "sticky more button" },
    });
  };

  const showCommentCount =
    !!renderCommentCount ||
    !!commentCountProps?.countId ||
    !!commentCountProps?.coralId;

  const commentCountClassName = clsx(
    styles.commentCount,
    showLoopNav && sharedStyles.layout.showTabletPlus,
  );

  const commentCount = showCommentCount
    ? (renderCommentCount?.(commentCountClassName) ??
      (commentCountProps ? (
        <CommentCount
          className={commentCountClassName}
          noText
          variant="stickyNav"
          {...commentCountProps}
        />
      ) : null))
    : null;

  const hamburgerButton = (
    <>
      {(showLoopNav || showCommentCount) && !showNotificationsButton && (
        <span
          className={clsx(
            styles.pipeSeparator,
            showLoopNav &&
              !showCommentCount &&
              sharedStyles.layout.hideTabletPlus,
          )}
        />
      )}
      <button
        onClick={handleMoreBtnClick}
        aria-controls={drawerId}
        aria-label="Open Drawer"
        className={clsx(
          styles.hamburgerNav,
          isBranded && styles.hamburgerBrandedNav,
        )}
      >
        <HamburgerIcon className={styles.moreIcon} />
      </button>
    </>
  );

  const notificationsButton = showNotificationsButton ? (
    <>
      {(showLoopNav || showCommentCount) && (
        <span
          className={clsx(
            styles.pipeSeparator,
            showLoopNav &&
              !showCommentCount &&
              sharedStyles.layout.hideTabletPlus,
          )}
        />
      )}
      <div className={styles.notificationsButtonContainer}>
        <NotificationsButton
          Logo={NotificationsLogo}
          popoverId="sticky"
          popoverClassName={notificationsClass}
        />
      </div>
    </>
  ) : null;

  useEffect(() => {
    if (!sticky) {
      return;
    }
    // Add sticky class to body, when sticky nav is visible.
    document.body.classList.add(SELECTORS.BodyStickyNav);
    return () => {
      // Remove sticky class from body.
      document.body.classList.remove(SELECTORS.BodyStickyNav);
    };
  }, [sticky]);

  return (
    <nav
      id="sticky-nav"
      aria-label="Sticky Nav"
      className={clsx(
        SELECTORS.StickyNav,
        styles.container,
        (overrideStickyVisible || sticky) && styles.sticky,
        activeCampaign && styles.campaignContainer,
        className,
      )}
    >
      {showLoopNav ? (
        <StickyNavLoopNavContent
          Logo={Logo}
          MinimalLogo={MinimalLogo}
          hamburgerButton={hamburgerButton}
          forceOpenLoopNav={forceOpenLoopNav}
          commentCount={commentCount}
        />
      ) : enableAltTitleInNav && (streamHeadline || packageHeadline) ? (
        <StickyNavAltTitleContent
          Logo={Logo}
          hamburgerButton={hamburgerButton}
          commentCount={commentCount}
          streamHeadline={streamHeadline}
          streamUrl={streamUrl}
          packageHeadline={packageHeadline}
          packageUrl={packageUrl}
          isLive={isLive}
        />
      ) : (
        <StickyNavStandardContent
          Logo={Logo}
          MinimalLogo={MinimalLogo}
          headline={headline}
          isBranded={isBranded}
          campaign={activeCampaign}
          hamburgerButton={hamburgerButton}
          commentCount={commentCount}
          notificationsButton={notificationsButton}
        />
      )}
    </nav>
  );
};

export default StickyNav;
